/**
 * The status line beneath the practice frame (feature 023, FR-032):
 * "ID: 1234 · Problem N of M · Streak: K 🔥 · Score: N · Difficulty D/10 · MPs",
 * with the review pin pushed to the right and the source tournament on a second
 * line.
 *
 * Streak and score answer different questions and are shown together on purpose
 * (client decision, 2026-07-21): the streak is the CURRENT run and restarts at
 * ±1 when the learner's luck turns, while the score is the visit's running total
 * and accumulates through zero. Neither substitutes for the other — a score of 0
 * could be a fresh start or ten right and ten wrong, and only the streak says
 * which way things are going right now.
 *
 * The identifier leads the line and is Admin/Editor-only — a learner has no use
 * for a database id, but an editor tracing a reported hand needs it first
 * (client decision, 2026-07-21). The gate lives with the caller, which knows the
 * role; this component simply omits the segment when given no value.
 *
 * "From: <tournament>" wraps onto its own full-width row because tournament
 * titles run long enough to push everything else off a single line.
 *
 * The pin is rendered ONLY for problem-based sessions. The review set stores
 * local problems (`users_problems_for_review` has a foreign key to
 * `problems(id)`), so a hand served by module practice has nothing to pin —
 * the client confirmed on 2026-07-20 that extending the review set is not
 * wanted. Module sessions therefore pass `showReviewPin={false}` and the
 * control is omitted rather than rendered dead. The reference design draws the
 * pin unconditionally; this is a deliberate divergence.
 */

import type { ReactNode } from 'react';

/**
 * The line's own chrome — spacing, type scale and the cream-on-felt colour — with no opinion
 * about what goes in it.
 *
 * Exported so a session whose progress is shaped differently (a fixed-length round counting
 * hands and correct answers, rather than a position in a pool) reads identically beneath the
 * frame without re-deriving these measurements or being bolted on to `StatusLine`'s props.
 * `data-testid="status-line"` rides with the shell, so the shared layout assertions hold for
 * any session that uses it.
 */
export function StatusLineShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 text-[13px] font-semibold text-sand"
      data-testid="status-line"
    >
      {children}
    </div>
  );
}

/** The separator between status segments. Decorative — never announced. */
export function StatusDot() {
  return <span aria-hidden="true">·</span>;
}

export interface StatusLineProps {
  /** 1-based position in the session. */
  position: number;
  /** Total questions available in this session/pool. */
  total: number;
  /**
   * The current run: positive for correct answers in a row, negative for wrong ones.
   *
   * Optional because a session need not keep one — a fixed-length round counting hands against a
   * target has no run to report, and rendering "Streak: 0" there would state something untrue
   * rather than something unknown. Both practice surfaces do keep one and pass it.
   */
  streak?: number;
  /**
   * The running total (+1 correct / −1 wrong), which accumulates through zero where the streak
   * restarts. Omit to show the streak alone.
   */
  score?: number;
  /**
   * The visit broken into its parts, shown after the score inside the same brass pill (feature
   * 028). Optional — omitted, the segment is not drawn, so the bidding surfaces read exactly as
   * before. Counting passes it: the pre-028 page showed correct/wrong/skipped beside the net
   * score, and a net of 0 does not say whether nothing was answered or half of it was wrong.
   */
  breakdown?: { correct: number; incorrect: number; skipped: number };
  /** Difficulty out of 10; omit when the item has none (e.g. tournament hands). */
  difficulty?: number | null;
  /**
   * What the identifier is called — "ID" for a tournament hand's tableresult id, "Serial" for a
   * curated problem's serial number. Ignored unless `idValue` is given.
   */
  idLabel?: 'ID' | 'Serial';
  /** The identifier itself. Omit (or pass null) to hide the segment — e.g. for non-editors. */
  idValue?: number | string | null;
  /** Canonical scoring label ("MPs", "IMPs", "BAM", "IMPs/MPs"). Omit to hide. */
  scoringType?: string | null;
  /** The tournament the hand came from; rendered on its own line as "From: …". */
  tournamentTitle?: string | null;
  /** Show the "Add to Review" pin. Problem-based sessions only. */
  showReviewPin?: boolean;
  /** Invoked when the pin is clicked. Required for the pin to be interactive. */
  onAddToReview?: () => void;
  /** True once this item is pinned, so the control reflects its state. */
  isPinned?: boolean;
  /** Disables the pin (e.g. while the request is in flight). */
  pinDisabled?: boolean;
}

export function StatusLine({
  position,
  total,
  streak,
  score,
  breakdown,
  difficulty,
  idLabel = 'ID',
  idValue,
  scoringType,
  tournamentTitle,
  showReviewPin = false,
  onAddToReview,
  isPinned = false,
  pinDisabled = false,
}: StatusLineProps) {
  return (
    <StatusLineShell>
      {idValue != null && (
        <>
          <span data-testid="status-id">
            {idLabel}: {idValue}
          </span>
          <StatusDot />
        </>
      )}

      <span data-testid="status-position">
        Problem {position} of {total}
      </span>

      <StatusDot />

      {(typeof streak === 'number' || typeof score === 'number') && (
        <span
          className="rounded-full bg-brass px-3 py-1 text-on-brass"
          data-testid="status-streak"
        >
          {typeof streak === 'number' && (
            <>
              {/* The flame belongs to a winning run only — beside "Streak: -3" it reads as praise. */}
              Streak: {streak} {streak > 0 && <span aria-hidden="true">🔥</span>}
            </>
          )}
          {typeof streak === 'number' && typeof score === 'number' && (
            <>
              {' '}
              <span aria-hidden="true">·</span>{' '}
            </>
          )}
          {typeof score === 'number' && (
            <span data-testid="status-score">Score: {score}</span>
          )}
          {breakdown && (
            <span data-testid="status-breakdown">
              {' '}
              <span aria-hidden="true">·</span> {breakdown.correct} correct{' '}
              <span aria-hidden="true">·</span> {breakdown.incorrect} wrong{' '}
              <span aria-hidden="true">·</span> {breakdown.skipped} skipped
            </span>
          )}
        </span>
      )}

      {typeof difficulty === 'number' && (
        <>
          <StatusDot />
          <span data-testid="status-difficulty">Difficulty {difficulty}/10</span>
        </>
      )}

      {scoringType && (
        <>
          <StatusDot />
          <span data-testid="status-scoring">{scoringType}</span>
        </>
      )}

      <span className="flex-1" />

      {showReviewPin && (
        <button
          type="button"
          onClick={onAddToReview}
          disabled={pinDisabled || isPinned}
          aria-pressed={isPinned}
          data-testid="status-review-pin"
          className={
            'rounded-full border border-brass bg-cream px-3 py-1 text-[12.5px] font-bold text-brass-text ' +
            'transition-colors hover:bg-paper focus:outline-none focus-visible:ring-2 ' +
            'focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-felt ' +
            'disabled:cursor-not-allowed disabled:opacity-60'
          }
        >
          {/* "Add to Review", not "Add to my Review Problems" (client request, 2026-07-30). The
              long form was four words of chrome on a line that already carries five segments, and
              its phone counterpart shares a button row with "Next Problem" where the width is the
              scarce thing. Both homes say the same words, as they always have. */}
          <span aria-hidden="true">📌</span> {isPinned ? 'In Review' : 'Add to Review'}
        </button>
      )}

      {tournamentTitle && (
        <span className="basis-full font-normal" data-testid="status-tournament">
          {/* The label is NOT dimmed: cream at 75% over the felt falls to 4.28:1, under AA. */}
          From: {tournamentTitle}
        </span>
      )}
    </StatusLineShell>
  );
}
